<template>
  <div>
    <side-bar title="Select monitors" icon="ondemand_video" search v-model="search">
      <template #buttons>
        <k-btn icon icon-name="play_circle" no-margin size="xs" v-tooltip.bottom="'Play all videos'" @click="$root.$emit('monitor2:play-all')"></k-btn>
        <k-btn icon icon-name="delete_sweep" no-margin size="xs" :disabled="!can_clear" v-tooltip.bottom="'Remove all videos and groups'" @click="clear_all"></k-btn>
        <k-btn v-if="$is_admin()" icon icon-name="filter" no-margin size="xs" :disabled="!todo_events.length" v-tooltip.bottom="'Add my todo events as groups'" @click="add_todo_events"></k-btn>
        <k-btn icon icon-name="library_add" no-margin size="xs" v-tooltip.bottom="'Add group'" @click="add_group"></k-btn>
      </template>

      <template v-slot:twosubheader>
        <k-tabs v-model="tab" grow style="margin-top: 8px; margin-bottom: 5px;">
          <k-tab name="events" label="Events"></k-tab>
          <k-tab name="input" label="Input"></k-tab>
          <k-tab name="output" label="Output"></k-tab>
          <k-tab name="system" label="System"></k-tab>
          <k-tab v-if="$is_admin()" name="instances" label="Instances"></k-tab>
        </k-tabs>
        <!-- Same instance groups as the dashboard -->
        <k-tabs v-if="tab == 'instances'" v-model="instance_tab" grow style="margin-bottom: 5px;">
          <k-tab name="input" label="Input"></k-tab>
          <k-tab name="output" label="Output"></k-tab>
          <k-tab name="system" label="System"></k-tab>
          <k-tab name="servers" label="Servers"></k-tab>
        </k-tabs>
      </template>

      <k-list v-if="tab == 'events'" key="events" bordered :items="filtered_events" two-line>
        <template #left="{ item }">
          <k-icon small :color="is_event_running(item) ? 'success' : 'primary'">circle</k-icon>
        </template>
        <template #default="{ item }">
          {{event_title(item)}}
        </template>
        <template #subtitle="{ item }">
          {{event_time(item)}}
        </template>
        <template #right="{ item }">
          <div class="monitor2-views">
            <k-menu offset-x min-width="0" :nudge-top="4" :nudge-right="2" :close-on-content-click="false">
              <template #activator>
                <k-btn
                  icon
                  icon-name="add"
                  no-margin
                  size="xs"
                  :class="{'monitor2-active': has_any_event_view(item)}"
                  v-tooltip.bottom="'Add source, preview or live'"
                ></k-btn>
              </template>
              <template #default>
                <div class="monitor2-views monitor2-views-menu">
                  <k-btn
                    v-for="view in views"
                    :key="view.value"
                    icon
                    :icon-name="view.icon"
                    no-margin
                    size="xs"
                    :class="{'monitor2-active': is_event_view_visible(item, view.value)}"
                    v-tooltip.bottom="view_tooltip(item, view)"
                    @click.stop="toggle_event_view(item, view.value)"
                  ></k-btn>
                  <span class="monitor2-views-divider"></span>
                  <k-btn
                    icon
                    icon-name="select_all"
                    no-margin
                    size="xs"
                    :class="{'monitor2-active': has_all_event_views(item)}"
                    v-tooltip.bottom="has_all_event_views(item) ? 'Remove all' : 'Add all'"
                    @click.stop="toggle_all_event_views(item)"
                  ></k-btn>
                </div>
              </template>
            </k-menu>
            <k-btn
              icon
              icon-name="dashboard_customize"
              no-margin
              size="xs"
              :class="{'monitor2-active': has_event_group(item)}"
              v-tooltip.bottom="has_event_group(item) ? 'Remove event group' : 'Add all as a group'"
              @click.stop="toggle_event_group(item)"
            ></k-btn>
          </div>
        </template>
      </k-list>

      <k-list v-else-if="tab == 'instances'" key="instances" bordered :items="filtered_instances" @click="toggle_instance" two-line>
        <template #left="{ item }">
          <k-icon small color="success">circle</k-icon>
        </template>
        <template #default="{ item }">
          {{instance_title(item)}}
        </template>
        <template #subtitle="{ item }">
          {{instance_channel_title(item)}}
        </template>
        <template #right="{ item }">
          <k-btn no-margin icon>
            <k-icon small v-if="is_visible(instance_key(item))" color="accent">visibility</k-icon>
            <k-icon small v-else>visibility_off</k-icon>
          </k-btn>
        </template>
      </k-list>

      <k-list v-else key="channels" bordered :items="filtered_channels" @click="toggle_channel" two-line>
        <template #left="{ item }">
          <k-icon small :color="is_channel_running(item) ? 'success' : 'primary'">circle</k-icon>
        </template>
        <template #default="{ item }">
          <channel-title grid :channel="item.channel" :alternative="item.alternative"></channel-title>
        </template>
        <template #subtitle="{ item }">
          <div style="text-transform: capitalize;">{{item.state}}</div>
        </template>
        <template #right="{ item }">
          <k-btn no-margin icon>
            <k-icon small v-if="is_visible(item.id)" color="accent">visibility</k-icon>
            <k-icon small v-else>visibility_off</k-icon>
          </k-btn>
        </template>
      </k-list>
    </side-bar>
  </div>
</template>

<script>
import naturalSort from 'natural-sort'
import datelib from '@/lib/date.js';
import ChannelModel from '@/models/channel.js';
import EventModel from '@/models/event.js';
import InstanceModel from '@/models/instance.js';
import { MONITOR2_EVENT_VIEWS, monitor2Items, monitor2ChannelKey, monitor2EventKey, monitor2InstanceKey, toggleMonitor2Items, addMonitor2Group, clearMonitor2, monitor2Groups, monitor2EventGroup, addMonitor2EventGroup, removeMonitor2EventGroup } from '@/models/monitor.js';
import SideBar from '@/components/SideBar.vue';
import ChannelTitle from '@/components/ChannelTitle.vue';
import eventsMixin from './events.js';

export default {
  mixins: [eventsMixin],
  components: {
    SideBar,
    ChannelTitle
  },
  data () {
    return {
      naturalSort: naturalSort(),
      search: "",
      tab: "events",
      instance_tab: "input",
      views: MONITOR2_EVENT_VIEWS,
      // Snapshot of `channels`, replaced only when a row changes (see the channels watcher)
      channel_items: []
    }
  },
  watch: {
    channels: {
      immediate: true,
      handler (items) {
        var prev = this.channel_items;
        var same = prev.length == items.length && items.every((item, i) => {
          var p = prev[i];
          return p.id == item.id && p.state == item.state && p.channel === item.channel;
        });
        if (!same) this.channel_items = items;
      }
    }
  },
  computed: {
    selected () {
      return monitor2Items(this.$store.state.account);
    },
    // Events on the account's own todo list that don't have a group yet and have something to show
    todo_events () {
      var account = this.$store.state.account;
      if (!account) return [];
      return _.sortBy(this.events.filter((event) => {
        return EventModel.is_todo_list(event, account) && !monitor2EventGroup(account, event.uuid);
      }), (e) => e.cached_start_time);
    },
    can_clear () {
      return this.selected.length > 0 || monitor2Groups(this.$store.state.account).length > 0;
    },
    filtered_events () {
      var events = this.events;
      if (this.search) {
        var s = this.search.toLowerCase();
        events = _.filter(events, (event) => this.event_title(event).toLowerCase().includes(s));
      }
      return _.sortBy(events, (e) => e.cached_start_time);
    },
    // Same result as ChannelModel.instance(channel).meta.is_developer, in one pass over the instances
    developer_channel_uuids () {
      var first = {};
      _.each(this.$store.state.instances, (instance) => {
        var uuid = _.get(instance, "meta.channel");
        if (uuid && !(uuid in first)) first[uuid] = _.get(instance, "meta.is_developer", false) == true;
      });
      return first;
    },
    // Active system channels; doesn't depend on channel_status, which is replaced on every status push
    system_channels () {
      return ChannelModel.list_active_channels().filter((channel) => {
        return _.get(channel, "meta.system_channel", false) == true && !this.developer_channel_uuids[channel.uuid];
      });
    },
    // Channels with a status entry, plus all active system channels without one.
    // Keys are normalized to "<uuid>:<alternative>" so "uuid" and "uuid:" are the same tile.
    channels () {
      var res = [];
      var seen = {};
      _.each(ChannelModel.status_all() || {}, (data, key) => {
        var uuid = key.split(":")[0];
        var alternative = key.split(":").slice(1).join(":");
        var id = monitor2ChannelKey(uuid + ":" + alternative);
        if (seen[id] || this.developer_channel_uuids[uuid]) return;
        var channel = ChannelModel.channel_by_uuid(uuid);
        if (!channel) return;
        seen[id] = true;
        seen[uuid] = true;
        res.push({id: id, channel: channel, alternative: alternative, state: _.get(data, "instance.source.state", "")});
      });
      this.system_channels.forEach((channel) => {
        if (seen[channel.uuid]) return;
        res.push({id: monitor2ChannelKey(channel.uuid + ":"), channel: channel, alternative: "", state: ""});
      });
      return res;
    },
    // Sorted lists per tab, so switching tabs only picks a ready list.
    // Uses channel_items so status pushes without visible changes don't re-render the list.
    channels_by_tab () {
      var tabs = {input: [], output: [], system: []};
      this.channel_items.forEach((item) => {
        var tab = _.get(item, "channel.meta.system_channel", false) == true ? "system" : item.channel.type;
        if (tabs[tab]) tabs[tab].push(item);
      });
      _.each(tabs, (list) => list.sort((a, b) => {
        return this.is_channel_running(b) - this.is_channel_running(a) || this.naturalSort(a.channel.title || "", b.channel.title || "");
      }));
      return tabs;
    },
    // Online instances grouped like in the dashboard instances list
    filtered_instances () {
      var type = this.instance_tab;
      var list;
      if (type == "servers") {
        list = _.concat(
          InstanceModel.instances_by_type("headend"),
          InstanceModel.instances_by_type("mediamtx"),
          InstanceModel.instances_by_type("clipper"),
          InstanceModel.instances_by_type("srt_server"),
          InstanceModel.instances_by_type("analyzer")
        );
      } else {
        list = type == "system"
          ? _.concat(InstanceModel.instances_by_type("input"), InstanceModel.instances_by_type("output"))
          : InstanceModel.instances_by_type(type);
        list = _.filter(list, (instance) => {
          var ch = InstanceModel.channel(instance);
          if (typeof(ch) == "undefined") return true;
          var system = _.get(ch, "meta.system_channel") === true;
          return type == "system" ? system : !system;
        });
      }
      list = _.compact(list);
      if (this.search) {
        var s = this.search.toLowerCase();
        list = _.filter(list, (instance) => {
          return this.instance_title(instance).toLowerCase().includes(s) || this.instance_channel_title(instance).toLowerCase().includes(s);
        });
      }
      return list.sort((a, b) => this.naturalSort(this.instance_title(a), this.instance_title(b)));
    },
    filtered_channels () {
      var channels = this.channels_by_tab[this.tab] || [];
      if (!this.search) return channels;
      var s = this.search.toLowerCase();
      return channels.filter((item) => (item.channel.title || "").toLowerCase().includes(s));
    }
  },
  methods: {
    clear_all () {
      if (!this.$store.state.account || !this.can_clear) return;
      if (!confirm("Remove all videos and groups from the monitor?")) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile", clearMonitor2(acc));
    },
    // Adds a group with the available source, preview and live videos for each todo event
    add_todo_events () {
      if (!this.$store.state.account || !this.todo_events.length) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.todo_events.forEach((event) => {
        addMonitor2EventGroup(acc, event.uuid, this.event_title(event), this.event_views(event));
      });
      this.$store.dispatch("update_profile", acc);
    },
    add_group () {
      if (!this.$store.state.account) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile", addMonitor2Group(acc));
    },
    update_items (keys) {
      if (!this.$store.state.account) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile", toggleMonitor2Items(acc, keys));
    },
    is_visible (key) {
      return this.selected.includes(key);
    },
    instance_key (instance) {
      return monitor2InstanceKey(instance.id);
    },
    instance_title (instance) {
      return InstanceModel.title(instance) || "";
    },
    instance_channel_title (instance) {
      return ChannelModel.title(InstanceModel.channel(instance)) || "";
    },
    toggle_instance (instance) {
      this.update_items([this.instance_key(instance)]);
    },
    toggle_channel (item) {
      this.update_items([item.id]);
    },
    is_channel_running (item) {
      return item.state == "connected";
    },
    event_title (event) {
      return EventModel.title(event);
    },
    event_time (event) {
      var v = event.cached_start_time;
      return datelib.format_date(v) + " - " + datelib.format_time(v);
    },
    is_event_running (event) {
      return EventModel.is_current(event, this.ts);
    },
    has_event_view (event, view) {
      if (view == "source") return !!EventModel.input_channel(event);
      if (view == "preview") return !!EventModel.preview_url(event);
      if (view == "live") return !!EventModel.live_url(event);
      return false;
    },
    // Tooltips render HTML, so the channel title is escaped
    view_tooltip (event, view) {
      if (view.value == "source") {
        var channel = EventModel.input_channel(event);
        return channel ? "Source: " + _.escape(channel.title) : "Source (no source channel yet)";
      }
      return this.has_event_view(event, view.value) ? view.title : view.title + " (no URL yet)";
    },
    has_any_event_view (event) {
      return this.views.some(view => this.is_event_view_visible(event, view.value));
    },
    has_all_event_views (event) {
      var views = this.event_views(event);
      return views.length > 0 && views.every(view => this.is_event_view_visible(event, view));
    },
    // Adds every available view, or removes them all when they are all added
    toggle_all_event_views (event) {
      if (!this.$store.state.account) return;
      var keys = this.event_views(event).map(view => monitor2EventKey(event.uuid, view));
      if (!keys.length) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile", toggleMonitor2Items(acc, keys, !this.has_all_event_views(event)));
    },
    is_event_view_visible (event, view) {
      return this.is_visible(monitor2EventKey(event.uuid, view));
    },
    toggle_event_view (event, view) {
      this.update_items([monitor2EventKey(event.uuid, view)]);
    },
    // All views can be added in advance; a tile shows its video once the URL or source channel exists
    event_views () {
      return this.views.map(v => v.value);
    },
    has_event_group (event) {
      return !!monitor2EventGroup(this.$store.state.account, event.uuid);
    },
    // Adds source, preview and live in a group titled after the event, or removes that group with its videos
    toggle_event_group (event) {
      if (!this.$store.state.account) return;
      var acc = _.cloneDeep(this.$store.state.account);
      if (monitor2EventGroup(acc, event.uuid)) {
        removeMonitor2EventGroup(acc, event.uuid);
      } else {
        addMonitor2EventGroup(acc, event.uuid, this.event_title(event), this.event_views(event));
      }
      this.$store.dispatch("update_profile", acc);
    }
  }
}
</script>

<style scoped>
.monitor2-views {
  display: flex;
  align-items: center;
  gap: 3px;
}
/* The theme has no accent color for icon buttons; the icon inherits the button color */
.monitor2-active {
  color: var(--ks-col-accent);
}
.monitor2-views-menu {
  padding: 4px 6px;
}
.monitor2-views-divider {
  align-self: stretch;
  width: 1px;
  margin: 2px 2px;
  background: var(--ks-col-secondary);
}
</style>
